import { fmtMoney } from "./format";

// The left rail, per `Juno Owner v4.dc.html` (founder addendum 30 Sep): YOU, the portfolio card with
// the marked-value line and the primary action, the two Release 2 shells, then consolidated activity.
//
// Nothing here computes a valuation. The chart is drawn from stored mark versions handed over by
// `projections.owner._portfolio_chart`, and the feed is one line per stored event. Both arrive ready.

const MONTH = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function monthLabel(day) {
  const m = Number(String(day || "").slice(5, 7));
  return MONTH[m - 1] || "";
}

// A mark HOLDS its value until the next version, so this is a STEP: flat to the next stored date,
// then a vertical step. No diagonals, no smoothing, nothing between versions.
export function StepChart({ points, note }) {
  if (!points || !points.length) return null;
  const xs = points.map((p) => Date.parse(p.day));
  const vs = points.map((p) => p.value);
  const [t0, t1] = [Math.min(...xs), Math.max(...xs)];
  const [v0, v1] = [Math.min(...vs), Math.max(...vs)];
  const x = (t) => (t1 === t0 ? 50 : ((t - t0) / (t1 - t0)) * 94 + 3);
  const y = (v) => (v1 === v0 ? 20 : 36 - ((v - v0) / (v1 - v0)) * 30);

  if (points.length === 1) {
    return (
      <div className="v4-chart" data-testid="portfolio-chart">
        <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="v4-chart-svg" aria-hidden="true">
          <circle cx={x(xs[0])} cy={y(vs[0])} r="1.6" className="v4-chart-dot" />
        </svg>
        <p className="v4-chart-note" data-testid="portfolio-chart-note">
          {note || `MARKED VALUE · ${monthLabel(points[0].day)}`}
        </p>
      </div>
    );
  }
  let line = `M ${x(xs[0])} ${y(vs[0])}`;
  for (let i = 1; i < points.length; i += 1) {
    line += ` L ${x(xs[i])} ${y(vs[i - 1])} L ${x(xs[i])} ${y(vs[i])}`;
  }
  line += ` L 100 ${y(vs[vs.length - 1])}`;                 // the standing mark holds to today
  const area = `${line} L 100 40 L ${x(xs[0])} 40 Z`;
  return (
    <div className="v4-chart" data-testid="portfolio-chart">
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="v4-chart-svg" aria-hidden="true">
        <defs>
          <linearGradient id="junoRailArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className="v4-chart-stop-top" />
            <stop offset="100%" className="v4-chart-stop-bottom" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#junoRailArea)" />
        <path d={line} className="v4-chart-line" fill="none" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="v4-chart-axis">
        <span>{monthLabel(points[0].day)}</span>
        <span>MARKED VALUE</span>
        <span>{monthLabel(points[points.length - 1].day)}</span>
      </div>
    </div>
  );
}

function when(at) {
  const t = Date.parse(at);
  // A date the server could not read comes through as null: show an em dash, never an empty gap, and
  // never drop the row (founder 30 Sep — a building must not disappear because of a date).
  if (Number.isNaN(t)) return "—";
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h`;
  const d = new Date(t);
  return `${d.getDate()} ${MONTH[d.getMonth()].slice(0, 3).toLowerCase()}`;
}

export function ActivityFeed({ activity, onOpen }) {
  const items = (activity && activity.items) || [];
  return (
    <div className="v4-feed" data-testid="activity-feed">
      <div className="v4-feed-head">
        <span className="v4-feed-title">CONSOLIDATED ACTIVITY</span>
        <span className="v4-feed-note">newest first</span>
      </div>
      {items.length === 0 ? (
        <p className="v4-feed-empty" data-testid="activity-empty">{activity.empty_line}</p>
      ) : (
        <div className="v4-feed-rows">
          {items.map((e, i) => (
            <button className="v4-feed-row" key={i} onClick={() => onOpen(e.building_ref)}
                    data-testid={"activity-" + e.kind + "-" + i}>
              <span className="v4-feed-top">
                <span className={"v4-feed-dot v4-feed-dot-" + e.tone} />
                <span className="v4-feed-agent">{e.agent}</span>
                <span className="v4-feed-t">{when(e.at)}</span>
              </span>
              <span className="v4-feed-text">{e.text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ShellCard({ label }) {
  return (
    <div className="v4-card v4-card-shell" data-testid={"rail-shell-" + label.toLowerCase().replace(/\s+/g, "-")}>
      <div className="v4-card-body">
        <div className="v4-card-top"><span className="v4-card-label">{label}</span></div>
        <div className="v4-card-stat">
          <span className="v4-card-num v4-card-num-inert">Release 2</span>
        </div>
      </div>
    </div>
  );
}

export function Rail({ desk, onOpen, onAdd }) {
  const s = desk.book.summary;
  const chart = desk.portfolio_chart || { points: [] };
  return (
    <aside className="v4-rail" data-testid="desk-rail">
      <div className="v4-you">
        <span className="v4-you-eyebrow">YOU</span>
        <span className="v4-you-name">{desk.identity.name}</span>
        {desk.identity.company && <span className="v4-you-sub">{desk.identity.company}</span>}
      </div>

      <div className="v4-card" data-juno-ink="dark" data-testid="portfolio-card">
        <span className="v4-card-edge" />
        <div className="v4-card-body">
          <div className="v4-card-top">
            <span className="v4-card-label">YOUR PORTFOLIO</span>
          </div>
          <div className="v4-card-stat">
            <span className="v4-card-num" data-testid="ys-value">
              {s.portfolio_value != null ? fmtMoney(s.portfolio_value) : "—"}
            </span>
            <span className="v4-card-unit">
              {s.portfolio_value != null ? "marked value" : "nothing marked yet"}
            </span>
          </div>
          <StepChart points={chart.points} note={chart.note} />
          <div className="v4-card-rows">
            <div className="v4-card-row">
              <span className="v4-card-row-dot" />
              <span className="v4-card-row-k">{s.buildings_owned === 1 ? "building" : "buildings"}</span>
              <span className="v4-card-row-v" data-testid="ys-buildings">{s.buildings_owned}</span>
            </div>
            <div className="v4-card-row">
              <span className="v4-card-row-dot" />
              <span className="v4-card-row-k">with a mark I can defend</span>
              <span className="v4-card-row-v" data-testid="ys-marked">
                {s.marked_count} of {s.buildings_owned}
              </span>
            </div>
          </div>
          <p className="v4-card-note">Blends exclude unmarked buildings; the count includes them.</p>
          <button className="v4-add v4-add-rail" data-testid="desk-add-building" onClick={onAdd}>
            Add a building
          </button>
        </div>
      </div>

      <ShellCard label="BUYER REQUIREMENTS" />
      <ShellCard label="DEALS IN MOTION" />
      <ActivityFeed activity={desk.activity || { items: [], empty_line: "" }} onOpen={onOpen} />
    </aside>
  );
}
